import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FeelingsAboutTherapyEnum } from 'src/generated/webapp_gql';
import { Button } from 'src/shared/components/Button';
import { color, font } from 'src/shared/styles/variables';

import committedIcon from 'src/public/img/icon_committed.svg';
import hesitantIcon from 'src/public/img/icon_hesitant.svg';
import neutralIcon from 'src/public/img/icon_neutral.svg';

const Wrapper = styled.div({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: 1000,
});

const Title = styled.h3({
    marginBottom: 40,
    color: color.pickledBluewood,
    ...font.black(font.size.headerExtended),
});

const List = styled.ul({
    padding: 0,
    display: 'flex',
});

interface CardProps {
    selected: boolean;
}

const Card = styled.li<CardProps>(props => ({
    maxWidth: 350,
    cursor: 'pointer',
    width: '100%',
    margin: '0 9px',
    display: 'flex',
    padding: 16,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    boxShadow: '0px 4px 12px rgba(218, 225, 234, 0.5)',
    border: `2px solid ${props.selected ? color.royalBlue : 'transparent'}`,
    borderRadius: 4,
    transition: '.3s',
}));

const CardHeader = styled.p({
    textAlign: 'center',
    marginTop: 10,
    color: color.pickledBluewood,
    ...font.heavy(font.size.mediumExtended),
});

const CardDescription = styled.p({
    textAlign: 'center',
    marginTop: 10,
    color: color.blueBayoux,
    lineHeight: '22px',
    ...font.book(font.size.base),
});

const ButtonsWrapper = styled.div({
    display: 'flex',
    marginTop: 40,
    width: '100%',
    maxWidth: 500,
    justifyContent: 'space-between',
});

const cards = [
    {
        image: committedIcon,
        header: 'therapy_motivation.committed.title',
        text: 'therapy_motivation.committed.description',
        value: FeelingsAboutTherapyEnum.Committed,
    },
    {
        image: neutralIcon,
        header: 'therapy_motivation.neutral.title',
        text: 'therapy_motivation.neutral.description',
        value: FeelingsAboutTherapyEnum.Neutral,
    },
    {
        image: hesitantIcon,
        header: 'therapy_motivation.hesitant.title',
        text: 'therapy_motivation.hesitant.description',
        value: FeelingsAboutTherapyEnum.Hesitant,
    },
];

export enum TherapyMotivationTestId {
    Continue = 'TherapyMotivationTestId.Continue',
    Previous = 'TherapyMotivationTestId.Previous',
}

interface Props {
    onContinuePress: () => void;
    onPreviousPress: () => void;
    onCardPress: (value: string) => void;
    feelingsAboutTherapy: string;
    loading?: boolean;
}

export const TherapyMotivation: React.FC<Props> = ({
    onContinuePress,
    onPreviousPress,
    feelingsAboutTherapy,
    onCardPress,
    loading,
}) => {
    const { t } = useTranslation();
    const dataValid = !!feelingsAboutTherapy;

    return (
        <Wrapper>
            <Title>{t('providers:therapy_motivation.title')}</Title>

            <List>
                {cards.map(card => (
                    <Card
                        key={card.value}
                        onClick={() => onCardPress(card.value)}
                        selected={feelingsAboutTherapy === card.value}
                    >
                        <img src={card.image} />
                        <CardHeader>{t(`providers:${card.header}`)}</CardHeader>
                        <CardDescription>{t(`providers:${card.text}`)}</CardDescription>
                    </Card>
                ))}
            </List>

            <ButtonsWrapper>
                <Button
                    testID={TherapyMotivationTestId.Previous}
                    onClick={onPreviousPress}
                    type="cancel"
                    widthPercentage={48}
                >
                    {t('common.previous')}
                </Button>
                <Button
                    testID={TherapyMotivationTestId.Continue}
                    onClick={onContinuePress}
                    type="accept"
                    widthPercentage={48}
                    withShadow={dataValid}
                    disabled={!dataValid}
                    loading={loading}
                >
                    {t('common.continue')}
                </Button>
            </ButtonsWrapper>
        </Wrapper>
    );
};
